import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Typography,
  Container,
  Button,
  Paper,
  Box,
} from '@mui/material';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useCatchCallback } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumInput from '../common/components/premium/PremiumInput';

const SharePage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const { id } = useParams();

  const device = useSelector((state) => state.devices.items?.[id]);

  const [expiration, setExpiration] = useState(dayjs().add(1, 'week').locale('en').format('YYYY-MM-DD'));
  const [link, setLink] = useState();

  const handleShare = useCatchCallback(async () => {
    const expirationTime = dayjs(expiration).toISOString();
    const response = await fetchOrThrow('/api/devices/share', {
      method: 'POST',
      body: new URLSearchParams(`deviceId=${id}&expiration=${expirationTime}`),
    });
    const token = await response.text();
    setLink(`${window.location.origin}?token=${token}`);
  }, [id, expiration, setLink]);

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['deviceShare']}>
      <Container maxWidth={false} className={classes.container}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: tokens.radius.lg,
            backgroundColor: tokens.surface.card,
            border: `1px solid ${tokens.border.subtle}`,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: tokens.text.primary }}>
            {t('sharedRequired')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <Box
              display="flex"
              width="100%"
              gap={2}
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
            >
              <Box flex={1} minWidth={0}>
                <PremiumInput
                  value={device?.name || ''}
                  label={t('sharedDevice')}
                  disabled
                />
              </Box>
              <Box flex={1} minWidth={0}>
                <PremiumInput
                  label={t('userExpirationTime')}
                  type="date"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleShare}
                  sx={{
                    borderRadius: tokens.radius.md,
                    borderColor: tokens.accent.primary,
                    '&:hover': {
                      borderColor: tokens.accent.hover,
                      backgroundColor: tokens.accent.subtle,
                    },
                  }}
                >
                  {t('reportShow')}
                </Button>
              </Box>
            </Box>

            <Box width="100%">
              <PremiumInput
                value={link || ''}
                onChange={(e) => setLink(e.target.value)}
                label={t('sharedLink')}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>
          </Box>
        </Paper>
        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: tokens.radius.md,
              borderColor: tokens.border.default,
              '&:hover': { borderColor: tokens.accent.primary },
            }}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={() => navigator.clipboard?.writeText(link)}
            disabled={!link}
            sx={{
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.accent.primary,
              '&:hover': { backgroundColor: tokens.accent.hover },
            }}
          >
            {t('sharedCopy')}
          </Button>
        </div>
      </Container>
    </PageLayout>
  );
};

export default SharePage;
